import { BottomSheetFlatList } from '@gorhom/bottom-sheet';
import React, { createRef, useMemo, useState } from 'react';
import {
  Platform,
  TouchableOpacity,
  useWindowDimensions,
  Image,
  LayoutChangeEvent,
} from 'react-native';
import { FlatList } from 'react-native-gesture-handler';

import CachedImage from '@components/CachedImage/CachedImage';
import DocumentCard from '@components/Chat/DocumentCard';
import { UploadProgressOverlay } from '@components/Chat/UploadProgressOverlay';
import PlayCircle from '@components/Icons/PlayCircle';
import { Box, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import Icon from '@components/shared/Icon/Icon';
import { Document, LocalFile } from '@graphql/generated';
import { useChatUploadManager } from '@hooks/useChatUploadManager';
import theme from '@themes/theme';

interface ChatImagesProps {
  list: (Document | LocalFile)[];
  onPress?: (image: Document | LocalFile) => void;
  onLongPress?: () => void;
  onDelete?: (image: LocalFile) => void;
  /** Renders the remove "x" as a dark circle with a white glyph (TA-10209 design) */
  darkDeleteIcon?: boolean;
  isForwarded?: boolean;
  showActivityIndicator?: boolean;
  isActivityItem?: boolean;
  numColumns?: number;
  isInsideBottomSheet?: boolean;
  hideSuccessIcon?: boolean;
  isTask?: boolean;
  isDocumentType?: boolean;
}

const ChatImages: React.FC<ChatImagesProps> = ({
  list,
  onPress,
  onLongPress,
  onDelete,
  darkDeleteIcon = false,
  isForwarded,
  showActivityIndicator,
  isActivityItem = false,
  numColumns = 1,
  isInsideBottomSheet = false,
  hideSuccessIcon,
  isTask = false,
  isDocumentType = false,
}) => {
  const _flatListRef = createRef<FlatList>();
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const windowWidth =
    Platform.select({
      native: Math.min(useWindowDimensions().width, 306),
      web: isActivityItem && Platform.OS === 'web' ? containerWidth : 306,
    }) ?? 306;

  const isChatInput = !!onDelete;
  const CHAT_INPUT_PADDING = 40;
  const CHAT_MESSAGE_PADDING = 94;
  // When using numColumns > 1 with isChatInput, use actual container width if available
  const shouldSubtractPadding = isChatInput && numColumns === 1;
  const outerPadding = isChatInput ? CHAT_INPUT_PADDING : CHAT_MESSAGE_PADDING;

  const _horizontalScrollRef = _flatListRef;

  const widthForWeb =
    isChatInput && numColumns > 1 && containerWidth
      ? containerWidth
      : windowWidth;

  // Use container width if available (for numColumns > 1), otherwise use calculated width
  // For native activity items the outer Box already measures the exact content
  // width, so we must NOT subtract the chat-message padding offset.
  let width: number;
  if (Platform.OS === 'web') {
    width = widthForWeb;
  } else if (isActivityItem) {
    width = containerWidth ?? 285;
  } else {
    width = (containerWidth ?? 285) - 18;
  }

  const largeWidth = shouldSubtractPadding
    ? width - outerPadding - (isForwarded ? 16 : 0)
    : width - (isForwarded ? 16 : 0); // FROM FIGMA
  const singleImage = list.length === 1;
  let imageRowLimit = 4;
  if (isChatInput && numColumns === 1) {
    imageRowLimit = 5;
  } else if (numColumns > 1) {
    imageRowLimit = numColumns;
  }
  if (isActivityItem && Platform.OS === 'web') {
    if (isTask) {
      numColumns = 4;
      imageRowLimit = 4;
    } else {
      numColumns = 8;
      imageRowLimit = 8;
    }
  }
  // Native activity feed: 3 per row, max 2 rows (6 items)
  const NATIVE_ACTIVITY_COLS = 3;
  const NATIVE_ACTIVITY_MAX = 6;
  if (isActivityItem && Platform.OS !== 'web') {
    numColumns = NATIVE_ACTIVITY_COLS;
    imageRowLimit = NATIVE_ACTIVITY_COLS;
  }
  const imageRowSpaces = imageRowLimit - 1;
  const maxBoxWidth = '100%';
  // Account for container padding when calculating multiImageWidth
  const containerPadding =
    isChatInput || isActivityItem ? 0 : theme.spacing.xs * 2;
  const availableWidth = largeWidth - containerPadding;
  const gapSpacing =
    isActivityItem && Platform.OS === 'web'
      ? theme.spacing.s
      : theme.spacing.xs;
  const isNativeActivity = isActivityItem && Platform.OS !== 'web';
  const rawMultiImageWidth =
    (availableWidth - gapSpacing * imageRowSpaces) / imageRowLimit;
  // TA-11043: native activity relies on flex-wrap producing exactly
  // NATIVE_ACTIVITY_COLS per row, because getMarginRight drops the gap on
  // every 3rd index. Yoga does its wrap math in float32, so a fractional
  // thumbnail width (e.g. 91.56) can sum past the measured width and push the
  // 3rd thumbnail onto row 2 (2+2 instead of 3+1), leaving it gapless.
  // Flooring to whole dp guarantees 3 thumbnails + 2 gaps always fit.
  const multiImageWidth = isNativeActivity
    ? Math.floor(rawMultiImageWidth)
    : rawMultiImageWidth;

  const size = singleImage && !isChatInput ? largeWidth : multiImageWidth;

  const { retryUpload, uploadingFiles } = useChatUploadManager();
  const uploadingStatusMap = useMemo(() => {
    if (uploadingFiles && uploadingFiles.length > 0) {
      return Object.fromEntries(
        uploadingFiles.map((f) => [
          f.file.clientId,
          {
            progress: f.progress ?? 0,
            compressionProgress: f.compressionProgress ?? 0,
            status: f.status ?? '',
          },
        ])
      );
    }
  }, [uploadingFiles]);

  const getMarginRight = (i: number): number => {
    if (isNativeActivity) {
      // 3-column grid: last item in each row gets no right margin
      return (i + 1) % NATIVE_ACTIVITY_COLS === 0 ? 0 : theme.spacing.xs;
    }
    if (isActivityItem && Platform.OS === 'web') {
      return i + 1 === imageRowLimit ? 0 : theme.spacing.s;
    }
    if ((i + 1) % imageRowLimit || Platform.OS === 'web') {
      return (largeWidth - multiImageWidth * imageRowLimit) / imageRowSpaces;
    } else {
      return 0;
    }
  };

  const handleContentChange = () => {
    if (list.length <= imageRowLimit) {
      // scroll to beginning since we are disabling scroll
      _horizontalScrollRef.current?.scrollToIndex({
        index: 0,
        animated: true,
      });
    }
  };

  const isDocument = (item: Document | LocalFile): item is Document => {
    return item.__typename === 'Document';
  };

  // TODO: Reduce this type down to Document, LocalFile is no longer used
  // Render image with thumbnail for videos and default behavior for images
  const renderImage = (item: Document | LocalFile, i: number) => {
    // TODO: Remove this note, it's inaccurate
    // NOTE: Image begins as a LocalFile instance that matches the shape of File type.
    // After processing the image attachment, a Document instance is returned and Document
    // type has a `file` field which is of type File.
    const isVideo = item.isVideo || item?.contentType?.includes('video');
    const isImage = item.isImage || item.contentType?.includes('image/');
    const isDoc = isDocument(item);
    let image = isDoc ? item.file : item;

    const clientId = item.clientId ?? item.id;
    const status = uploadingStatusMap?.[clientId]?.status;
    const compressionProgress =
      uploadingStatusMap?.[clientId]?.compressionProgress ?? 0;
    const uploadProgress = uploadingStatusMap?.[clientId]?.progress ?? 0;
    const combinedProgress =
      status === 'compressing'
        ? compressionProgress * 0.5
        : 0.5 + uploadProgress * 0.5;
    const blobId = item.blobId ?? '';

    const isLocal =
      image?.url?.startsWith('file:') ||
      image?.url?.startsWith('data:') ||
      image?.path?.startsWith('file:') ||
      image?.path?.startsWith('data:');

    if (isLocal && image?.path) {
      image = { ...image, url: image.path };
    }

    showActivityIndicator ??= !isChatInput && isLocal;
    const thumbnailUri = image?.thumbnail;

    let width = size;
    if (isActivityItem && Platform.OS === 'web') {
      if (singleImage) width = 248;
      if (i + 1 > imageRowLimit) return null;
    }
    // Native activity feed: hide items beyond the 6-item cap
    if (isActivityItem && Platform.OS !== 'web') {
      if (i >= NATIVE_ACTIVITY_MAX) return null;
    }

    // Height rules:
    //   native activity + single image  → 136
    //   native activity + multi          → 64
    //   web activity (image/video)       → 118
    //   all other                        → size (square)
    let activityHeight = 118;
    if (isActivityItem && Platform.OS !== 'web') {
      activityHeight = singleImage ? 136 : 64;
    }
    const height = isVideo || isImage ? activityHeight : multiImageWidth;
    const isLast = list.length < 4 || (list.length > 3 && i > 2);

    let itemHeight: number;
    if (Platform.OS === 'web') {
      itemHeight = (isActivityItem && singleImage ? height : size) - 2;
    } else if (isActivityItem) {
      itemHeight = singleImage ? 136 : 64;
    } else {
      itemHeight = size;
    }

    return (
      <TouchableOpacity
        key={item.id ?? image?.id}
        // disabled={isChatInput}
        onPress={() => onPress?.(item)}
        onLongPress={() => onLongPress?.()}
        style={[
          {
            width: Platform.OS === 'web' ? width - 2 : size,
            height: itemHeight,
            flexDirection: 'row',
            marginRight: isChatInput ? theme.spacing.xs : getMarginRight(i),
            marginBottom:
              (isActivityItem && Platform.OS === 'web') ||
              (isActivityItem && Platform.OS !== 'web' && isLast)
                ? 0
                : theme.spacing.xs,
          },
        ]}>
        <Box
          accessibilityLabel='Media File'
          justifyContent='center'
          alignItems='center'
          pointerEvents='auto'
          marginTop={darkDeleteIcon ? 'xs' : 'none'}>
          {isVideo && thumbnailUri ? (
            <Image
              accessibilityLabel={item.name}
              source={{ uri: thumbnailUri ?? image.cdnBaseUrl }}
              style={{
                width: width,
                height: (() => {
                  if (isActivityItem && Platform.OS === 'web' && singleImage)
                    return 118;
                  if (isActivityItem && Platform.OS !== 'web')
                    return singleImage ? 136 : 64;
                  return size;
                })(),
                borderRadius: theme.spacing.xs,
              }}
              resizeMode='cover'
            />
          ) : (
            <Box
              accessibilityLabel={item.name}
              mb={isImage || isActivityItem ? undefined : 'xs'}>
              {isImage ? (
                <CachedImage
                  imageClientId={item.clientId}
                  image={image}
                  width={width}
                  height={(() => {
                    if (isActivityItem && Platform.OS === 'web' && singleImage)
                      return 118;
                    if (isActivityItem && Platform.OS !== 'web')
                      return singleImage ? 136 : 64;
                    return size;
                  })()}
                  from='search'
                  isRemoveable={!!onDelete}
                  borderRadius={theme.spacing.xs}
                  contentType={item.contentType}
                />
              ) : (
                <DocumentCard
                  isActivityItem={isActivityItem}
                  width={
                    isActivityItem && Platform.OS === 'web' && singleImage
                      ? multiImageWidth
                      : width
                  }
                  height={(() => {
                    if (isActivityItem && Platform.OS === 'web' && singleImage)
                      return multiImageWidth;
                    if (isActivityItem && Platform.OS !== 'web')
                      return singleImage ? 136 : 64;
                    return size;
                  })()}
                  key={item.id}
                  document={item}
                  onPress={() => {
                    onPress?.(item);
                  }}
                  onLongPress={() => onLongPress?.()}
                />
              )}
            </Box>
          )}

          {isActivityItem &&
            Platform.OS !== 'web' &&
            i === NATIVE_ACTIVITY_MAX - 1 &&
            list.length > NATIVE_ACTIVITY_MAX && (
              <Box
                position='absolute'
                width={size}
                height={64}
                justifyContent='center'
                alignItems='center'
                borderRadius='xs'
                overflow='hidden'
                style={{ backgroundColor: theme.colors.grey06 }}>
                <Text
                  variant='labelEmphasized'
                  color='white'
                  lineHeight={21}
                  style={{ opacity: 1 }}>
                  {'+' + (list.length - (NATIVE_ACTIVITY_MAX - 1))}
                </Text>
              </Box>
            )}

          {isActivityItem &&
            Platform.OS === 'web' &&
            i === imageRowLimit - 1 &&
            list.length > imageRowLimit && (
              <Box
                position='absolute'
                width={width}
                height={size}
                justifyContent='center'
                alignItems='center'
                borderRadius='xs'
                overflow='hidden'
                style={{
                  // theme.colors.grey06 = 'rgba(0,0,0,0.5)' — opacity encoded
                  // in the background so the Text child stays fully opaque.
                  backgroundColor: theme.colors.grey06,
                }}>
                <Text
                  variant='labelEmphasized'
                  color='white'
                  lineHeight={21}
                  style={{ opacity: 1 }}>
                  {'+' + (list.length - imageRowLimit + 1)}
                </Text>
              </Box>
            )}

          {isVideo && <PlayCircle />}

          <UploadProgressOverlay
            showProgressBar={status === 'compressing' || status === 'uploading'}
            progress={combinedProgress}
            showSuccessIcon={
              !hideSuccessIcon &&
              isLocal &&
              (status === 'success' || blobId != '')
            }
            showErrorRetry={status === 'error'}
            onRetry={() => retryUpload?.(item as LocalFile)}
          />

          {showActivityIndicator && !status && (
            <ActivityIndicatorLoading imageWidth={size} />
          )}

          {isChatInput && (
            <TouchableOpacity
              onPress={() => image && onDelete(image as LocalFile)}
              style={{
                position: 'absolute',
                // Badge straddles the thumbnail's top-right corner (Figma 154:29202)
                top: darkDeleteIcon ? -theme.spacing.xs : 0,
                right: darkDeleteIcon ? -theme.spacing.xs : theme.spacing.xxxs,
              }}
              hitSlop={{ top: 10, bottom: 10, right: 10, left: 10 }}>
              <Icon
                name={darkDeleteIcon ? 'X2Dark' : 'X2'}
                variant={darkDeleteIcon ? 'm' : 'l'}
                marginTop={darkDeleteIcon ? undefined : 'xxxs'}
                color='textPrimary'
              />
            </TouchableOpacity>
          )}
        </Box>
      </TouchableOpacity>
    );
  };

  const handleLayout = (event: LayoutChangeEvent) => {
    const { width: layoutWidth } = event.nativeEvent.layout;
    // Native activity sizes thumbnails from this width, so it must track
    // later width changes (rotation, split view) — a stale, wider value would
    // overflow and wrap the grid (TA-11043). Other modes keep first-measure.
    const shouldMeasure = isNativeActivity || !containerWidth;
    if (shouldMeasure && layoutWidth !== containerWidth) {
      setContainerWidth(layoutWidth);
    }
  };

  const renderListContent = () => {
    if (!isChatInput || Platform.OS === 'web') {
      return list.map((item: Document | LocalFile, i) => renderImage(item, i));
    }

    if (isInsideBottomSheet) {
      return (
        <BottomSheetFlatList
          keyboardShouldPersistTaps='always'
          data={Array.isArray(list) ? list : []}
          horizontal={numColumns === 1}
          numColumns={numColumns}
          scrollEnabled={list.length > imageRowLimit}
          keyExtractor={(item) => item.id}
          renderItem={({ item, index }) => renderImage(item, index)}
          ListFooterComponent={() => <Box marginRight='xs' />}
          ListHeaderComponent={() =>
            numColumns === 1 ? <Box marginRight='m' /> : null
          }
          onContentSizeChange={handleContentChange}
        />
      );
    }

    return (
      <FlatList
        ref={_flatListRef}
        keyboardShouldPersistTaps='always'
        data={Array.isArray(list) ? list : []}
        horizontal={numColumns === 1}
        numColumns={numColumns}
        scrollEnabled={list.length > imageRowLimit}
        keyExtractor={(item) => item.id}
        renderItem={({ item, index }) =>
          containerWidth || Platform.OS === 'web'
            ? renderImage(item, index)
            : null
        }
        ListFooterComponent={() => <Box marginRight='xs' />}
        ListHeaderComponent={() =>
          numColumns === 1 ? <Box marginRight='m' /> : null
        }
        onContentSizeChange={handleContentChange}
      />
    );
  };

  let paddingTopValue: 'xxs' | 'none' | 'xs' = 'xs';
  if (isDocumentType) {
    paddingTopValue = Platform.OS === 'web' ? 'xxs' : 'none';
  }

  return (
    <Box
      flexDirection='row'
      flexWrap={isChatInput && Platform.OS !== 'web' ? 'nowrap' : 'wrap'}
      paddingTop={paddingTopValue}
      paddingRight='none'
      paddingHorizontal={
        !isActivityItem && (!onDelete || Platform.OS === 'web')
          ? 'xs'
          : undefined
      }
      borderRadius='xs'
      justifyContent='flex-start'
      borderWidth={
        (!isChatInput || Platform.OS === 'web') && !isActivityItem ? 1 : 0
      }
      width={maxBoxWidth}
      borderColor='grey02'
      onLayout={handleLayout}>
      {renderListContent()}
    </Box>
  );
};

export default ChatImages;
